import { forwardRef, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box2, BufferGeometry, Group, Layers, Matrix3, Vector2, Vector3 } from 'three';
import { Plane } from 'three';
import { clamp } from 'three/src/math/MathUtils.js';

import { Html, Line, useCursor } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { getClosestPointOnNavMesh, NavMeshQuery } from '@spazious/dollhouse-navmesh';
import { DOLLHOUSE_LAYER_INTERACTIVE, toVector2 } from '@spazious/dollhouse-three';
import { useEventCallback, useForkRef } from '@spazious/react-hooks';

import { FCompositionNode } from '../../fcomposition/types.js';

import {
  RectControlsResizeHandler,
  RectControlsResizeHandlerProps,
} from './RectControlsResizeHandler.js';

export interface DragChangeEvent {
  originalEvent: Event | null;
  center: [number, number];
  size: [number, number];
  theta: number;
}

export interface RectControlsProps {
  center?: Vector2 | [number, number];
  size?: Vector2 | [number, number];
  minSize?: Vector2 | [number, number];
  maxSize?: Vector2 | [number, number];
  theta?: number;
  navMeshQuery?: NavMeshQuery;
  onChange?: (evt: DragChangeEvent) => void;
  mirror?: boolean;
  userData?: {
    node: FCompositionNode;
  };
  labelFormatter?: (value: number | string) => string | number;
}

const _fbox = new Box2();
const _center = new Vector2();

/**
 *
 */
export const RectControls = forwardRef<Group, RectControlsProps>(
  (
    {
      center = [0, 0],
      size = [1, 1],
      theta = 0,
      onChange,
      minSize: minSizeProp = [0, 0],
      maxSize: maxSizeProp = [Infinity, Infinity],
      navMeshQuery,
      labelFormatter,
      ...restProps
    },
    ref
  ) => {
    const camera = useThree(({ camera }) => camera);
    const controls = useThree(({ controls }) => controls);
    const raycaster = useThree(({ raycaster }) => raycaster);
    const pointer = useThree(({ pointer }) => pointer);

    const minSize = useMemo(() => toVector2(minSizeProp, new Vector2()), [minSizeProp]);
    const maxSize = useMemo(() => toVector2(maxSizeProp, new Vector2()), [maxSizeProp]);

    const plane = useMemo(() => new Plane(camera.up.clone().negate(), 0), [camera.up]);

    const box = useMemo(
      () => new Box2().setFromCenterAndSize(new Vector2(0, 0), toVector2(size, new Vector2())),
      [size]
    );

    const boxCenter = useMemo(() => box.getCenter(new Vector2()), [box]);

    const vertices = useMemo(() => {
      const { min, max } = box;
      return [
        new Vector2(min.x, min.y),
        new Vector2(max.x, min.y),
        new Vector2(max.x, max.y),
        new Vector2(min.x, max.y),
      ];
    }, [box]);

    const points = useMemo(() => [...vertices, vertices[0]], [vertices]);

    const rectGeometry = useMemo(
      () =>
        new BufferGeometry().setFromPoints([
          vertices[0],
          vertices[2],
          vertices[1],

          vertices[0],
          vertices[3],
          vertices[2],
        ]),
      [vertices]
    );

    const groupRef = useRef<Group>(null);

    const forkedRef = useForkRef<Group>(ref, groupRef);

    useLayoutEffect(() => {
      groupRef.current?.lookAt(plane.normal);
    }, [plane.normal]);

    const color = '#6c81d3';
    const labelTextColor = '#ffffff';
    const lineWidth = 2;
    const handlerLineWidthFactor = 2;
    const handlerLineWidth = handlerLineWidthFactor * lineWidth;
    const labelOffset = { x: 0.6, y: 0.5 };
    const resizeHandlerOffset = 0.07;

    const bottomLineDistance = useMemo(() => {
      const [bottomLeft, bottomRight] = [vertices[0], vertices[1]];
      return Math.sqrt(
        Math.pow(bottomRight.x - bottomLeft.x, 2) + Math.pow(bottomRight.y - bottomLeft.y, 2)
      );
    }, [vertices]);

    const rightLineDistance = useMemo(() => {
      const [bottomRight, topRight] = [vertices[1], vertices[2]];
      return Math.sqrt(
        Math.pow(topRight.x - bottomRight.x, 2) + Math.pow(topRight.y - bottomRight.y, 2)
      );
    }, [vertices]);

    const bindResizeHandler = useCallback(
      (
          cardinal: 'n' | 's' | 'e' | 'w' | 'ne' | 'sw' | 'nw' | 'se'
        ): PickProp<RectControlsResizeHandlerProps, 'onDrag'> =>
        ({ down, first, event }) => {
          if (first) {
            _fbox.copy(
              new Box2().setFromCenterAndSize(
                toVector2(center, _center),
                toVector2(size, new Vector2())
              )
            );
          }
          if (controls) {
            (controls as any).enabled = !down;
          }

          const projected = new Vector3();

          raycaster.setFromCamera(pointer, camera);
          raycaster.ray.intersectPlane(plane, projected);

          // project to nearest point on navmesh
          getClosestPointOnNavMesh(projected, projected, navMeshQuery);

          const ppoint = new Vector2(projected.x, projected.z)
            .sub(_center)
            .applyMatrix3(new Matrix3().makeRotation(theta));

          const rbox = new Box2().setFromCenterAndSize(
            new Vector2(0, 0),
            _fbox.getSize(new Vector2())
          );

          switch (cardinal) {
            case 'w':
            case 'nw':
            case 'sw':
              rbox.min.x = ppoint.x;
              break;
            case 'e':
            case 'ne':
            case 'se':
              rbox.max.x = ppoint.x;
              break;
          }

          switch (cardinal) {
            case 'n':
            case 'ne':
            case 'nw':
              rbox.max.y = ppoint.y;
              break;
            case 's':
            case 'se':
            case 'sw':
              rbox.min.y = ppoint.y;
              break;
          }

          rbox.min.x = clamp(rbox.min.x, rbox.max.x - maxSize.x, rbox.max.x - minSize.x);
          rbox.max.x = clamp(rbox.max.x, rbox.min.x + minSize.x, rbox.min.x + maxSize.x);
          rbox.min.y = clamp(rbox.min.y, rbox.max.y - maxSize.y, rbox.max.y - minSize.y);
          rbox.max.y = clamp(rbox.max.y, rbox.min.y + minSize.y, rbox.min.y + maxSize.y);

          const m = new Matrix3().makeRotation(-theta);

          onChange?.({
            originalEvent: event,
            center: rbox.getCenter(new Vector2()).applyMatrix3(m).add(_center).toArray(),
            size: rbox.getSize(new Vector2()).toArray(),
            theta,
          });
        },
      [
        camera,
        center,
        controls,
        maxSize.x,
        maxSize.y,
        minSize.x,
        minSize.y,
        onChange,
        plane,
        pointer,
        raycaster,
        size,
        theta,
        navMeshQuery,
      ]
    );

    const layers = useMemo(() => {
      const layers = new Layers();
      layers.enable(DOLLHOUSE_LAYER_INTERACTIVE);
      return layers;
    }, []);

    const [hovered, setHover] = useState(false);
    useCursor(hovered);

    const handlePointerMove = useEventCallback(() => {
      setHover(true);
    });

    const handlePointerOut = useEventCallback(() => {
      setHover(false);
    });

    return (
      <group ref={forkedRef} renderOrder={1000}>
        <group
          position={[toVector2(center, new Vector2()).x, toVector2(center, new Vector2()).y, -0.1]}
          rotation={[0, 0, -theta]}
        >
          <mesh
            geometry={rectGeometry}
            layers={layers}
            {...restProps}
            onPointerMove={handlePointerMove}
            onPointerOut={handlePointerOut}
          >
            <meshBasicMaterial
              polygonOffset
              transparent
              color={color}
              opacity={0.3}
              polygonOffsetUnits={-10}
            />
          </mesh>

          <Line
            polygonOffset
            color={color}
            dashed={false}
            depthTest={false}
            lineWidth={lineWidth}
            points={points}
            polygonOffsetUnits={-20}
            renderOrder={1004}
          />

          <Html
            ref={(node) => {
              if (node?.parentElement) {
                node.parentElement.style.pointerEvents = 'none';
              }
            }}
            center
            position={[boxCenter.x, box.max.y + labelOffset.y, 0]}
            style={{
              backgroundColor: color,
              color: labelTextColor,
              padding: 'var(--spacing-padding)',
              borderRadius: 'var(--shape-rounded-radius)',
              whiteSpace: 'nowrap',
            }}
          >
            {labelFormatter?.(bottomLineDistance)}
          </Html>
          <Html
            ref={(node) => {
              if (node?.parentElement) {
                node.parentElement.style.pointerEvents = 'none';
              }
            }}
            center
            position={[box.max.x + labelOffset.x, boxCenter.y, 0]}
            style={{
              backgroundColor: color,
              color: labelTextColor,
              padding: 'var(--spacing-padding)',
              borderRadius: 'var(--shape-rounded-radius)',
              whiteSpace: 'nowrap',
            }}
          >
            {labelFormatter?.(rightLineDistance)}
          </Html>
          <group renderOrder={1005}>
            {/* Edges */}
            <RectControlsResizeHandler
              cursor="ns-resize"
              lineWidth={handlerLineWidth}
              name="resize-handler-edge-n"
              position={[boxCenter.x, box.max.y + resizeHandlerOffset, 0]}
              onDrag={bindResizeHandler('n')}
            />
            <RectControlsResizeHandler
              cursor="ew-resize"
              lineWidth={handlerLineWidth}
              name="resize-handler-edge-e"
              position={[box.max.x + resizeHandlerOffset, boxCenter.y, 0]}
              rotation={[0, 0, Math.PI * 0.5]}
              onDrag={bindResizeHandler('e')}
            />
            <RectControlsResizeHandler
              cursor="ns-resize"
              lineWidth={handlerLineWidth}
              name="resize-handler-edge-s"
              position={[boxCenter.x, box.min.y - resizeHandlerOffset, 0]}
              onDrag={bindResizeHandler('s')}
            />
            <RectControlsResizeHandler
              cursor="ew-resize"
              lineWidth={handlerLineWidth}
              name="resize-handler-edge-w"
              position={[box.min.x - resizeHandlerOffset, boxCenter.y, 0]}
              rotation={[0, 0, Math.PI * 0.5]}
              onDrag={bindResizeHandler('w')}
            />
            {/* Corners */}
            <RectControlsResizeHandler
              corner
              cursor="nwse-resize"
              lineWidth={handlerLineWidth}
              name="resize-handler-corner-ne"
              position={[box.max.x + resizeHandlerOffset, box.max.y + resizeHandlerOffset, 0]}
              onDrag={bindResizeHandler('ne')}
            />
            <RectControlsResizeHandler
              corner
              cursor="nwse-resize"
              lineWidth={handlerLineWidth}
              name="resize-handler-corner-sw"
              position={[box.min.x - resizeHandlerOffset, box.min.y - resizeHandlerOffset, 0]}
              rotation={[0, 0, Math.PI]}
              onDrag={bindResizeHandler('sw')}
            />
            <RectControlsResizeHandler
              corner
              cursor="nesw-resize"
              lineWidth={handlerLineWidth}
              name="resize-handler-corner-nw"
              position={[box.min.x - resizeHandlerOffset, box.max.y + resizeHandlerOffset, 0]}
              rotation={[0, 0, Math.PI * 0.5]}
              onDrag={bindResizeHandler('nw')}
            />
            <RectControlsResizeHandler
              corner
              cursor="nesw-resize"
              lineWidth={handlerLineWidth}
              name="resize-handler-corner-se"
              position={[box.max.x + resizeHandlerOffset, box.min.y - resizeHandlerOffset, 0]}
              rotation={[0, 0, -Math.PI * 0.5]}
              onDrag={bindResizeHandler('se')}
            />
          </group>
        </group>
      </group>
    );
  }
);

export default RectControls;
